import { createContext, useContext, useState } from 'react';
import { useEffect } from 'react';

import {
  AlertCircleIcon,
  ArrowDown01Icon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Collapse } from 'react-bootstrap';

import { IconComponent } from 'components/Icon/IconComponent';

import styles from './CollapsibleBlock.module.scss';

const VARIANT_ICON_CONFIG = {
  error: { icon: AlertCircleIcon, type: 'error' },
  success: { icon: CheckmarkCircle02Icon, type: 'success' },
  info: { icon: InformationCircleIcon, type: 'info', size: 'small' },
  warning: { icon: AlertCircleIcon, type: 'warning' },
  neutral: { icon: InformationCircleIcon, type: 'info', size: 'small' },
};

const CollapsibleGroupContext = createContext(null);

export const CollapsibleGroup = ({ newStartOpen, children }) => {
  const [selectedKey, setSelectedKey] = useState();

  const contextValue = { selectedKey, newStartOpen, setSelectedKey };

  return (
    <CollapsibleGroupContext.Provider value={contextValue}>
      {children}
    </CollapsibleGroupContext.Provider>
  );
};

const CollapsibleContext = createContext(null);

/** FormMessage-aligned variants: Error, Success, Info, Warning, Neutral (same border/background as FormMessage) */
export const Variant = {
  Error: 'error',
  Success: 'success',
  Info: 'info',
  Warning: 'warning',
  Neutral: 'neutral',
};

const variantToClass = v =>
  v ? styles[`rootVariant${v.charAt(0).toUpperCase() + v.slice(1)}`] : null;

export const CollapsibleBlock = ({
  startOpen = false,
  keyCode,
  children,
  color,
  className,
  open,
  variant,
}) => {
  const collapsibleGroup = useContext(CollapsibleGroupContext);

  // Set key if in CollapsibleGroup and opened to close other Blocks
  useEffect(() => {
    if (!collapsibleGroup) return;
    setOpened(collapsibleGroup?.selectedKey === keyCode);
  }, [collapsibleGroup?.selectedKey]);

  // Open on creation if property set on CollapsibleGroup
  useEffect(() => {
    if (!keyCode || !collapsibleGroup || !collapsibleGroup.newStartOpen) return;
    handleToggle();
  }, []);

  const [opened, setOpened] = useState(startOpen);
  const [hasContent, setHasContent] = useState(false);

  const handleToggle = () => {
    if (open) {
      return;
    }
    if (!opened && collapsibleGroup) collapsibleGroup.setSelectedKey(keyCode);
    setOpened(!opened);
  };

  const contextValue = { opened, hasContent, onToggle: handleToggle, setHasContent, variant };

  useEffect(() => {
    if (open) {
      setOpened(open);
    }
  }, [open]);

  // When no variant: match original exactly — inline left border when color is set
  const rootStyle =
    !variant && color != null && color !== '' ? { borderLeft: `3px solid ${color}` } : undefined;

  return (
    <CollapsibleContext.Provider value={contextValue}>
      <div
        className={cn(styles.root, 'card', 'card-with-border', variantToClass(variant), {
          [styles.rootOpened]: opened,
          [className]: className,
        })}
        style={rootStyle}>
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
};

const Header = ({ children, fullWith, alignStart = false, showVariantIcon = true }) => {
  const { onToggle, hasContent, variant } = useContext(CollapsibleContext);

  const config = variant && VARIANT_ICON_CONFIG[variant];
  const showIcon = variant && showVariantIcon && config;

  const headerContent = showIcon ? (
    <div className={styles.headerVariantContentWrap}>
      <div className={styles.headerVariantIconWrap}>
        <IconComponent icon={config.icon} noBackground type={config.type} size={config.size} />
      </div>
      {children}
    </div>
  ) : (
    children
  );

  return (
    <header
      className={cn(styles.header, {
        [styles.headerFullWith]: fullWith,
        [styles.alignStart]: alignStart,
      })}>
      {headerContent}
      {hasContent ? (
        <IconComponent
          className={styles.toggleButton}
          icon={ArrowDown01Icon}
          onClick={onToggle}
          noBackground
          type="secondary"
          size="large"
          strokeWidth={1.5}
        />
      ) : (
        <div className={styles.emptyBlock} />
      )}
    </header>
  );
};

const Content = ({ children, className }) => {
  const { opened, setHasContent } = useContext(CollapsibleContext);

  useEffect(() => setHasContent(!!children), [children]);

  return (
    <Collapse in={opened}>
      <div className={className}>{children}</div>
    </Collapse>
  );
};

CollapsibleBlock.Header = Header;
CollapsibleBlock.Content = Content;

CollapsibleBlock.propTypes = {
  keyCode: PropTypes.string,
  className: PropTypes.string,
  startOpen: PropTypes.bool,
  color: PropTypes.string,
  /** FormMessage-aligned variant: error, success, info, warning, neutral. Use with same icons as FormMessage for consistent look. */
  variant: PropTypes.oneOf(['error', 'success', 'info', 'warning', 'neutral']),
};

CollapsibleBlock.Variant = Variant;
